import type { PostingView } from '@nest-recruit/shared';
import type { StatusTone } from '../../../../components/Tone';
import { isoToDhaka } from '../../../../lib/dhaka-time';
import { formatIsoDate } from '../../../../lib/format';

/**
 * Form links (DECISIONS-LOG 2026-10-27): where one link stands, in the words and colour the
 * Form links tab shows. Green open, amber on its last two days, blue not open yet, purple
 * closed, as drawn (artifact HixuiyeD5X7MnNEQnm5ZoX, section 3).
 */
export type FormLinkPhase = 'scheduled' | 'open' | 'closing' | 'paused' | 'closed';

export interface FormLinkStatus {
  readonly phase: FormLinkPhase;
  readonly label: string;
  readonly tone: StatusTone;
}

const MS_PER_DAY = 86_400_000;

/** A link closes at the end of its last day, BD time (D14). */
export const LINK_CLOSING_CLOCK = '23:59';
const BD_OFFSET = '+06:00';

export function formLinkStatus(
  link: Pick<PostingView, 'status' | 'unpublishReason' | 'goesLiveOn' | 'expiresOn' | 'closedOn' | 'applicants' | 'maxApplications'>,
  today: string,
): FormLinkStatus {
  if (link.status === 'draft') {
    return { phase: 'scheduled', label: link.goesLiveOn ? `Opens ${formatIsoDate(link.goesLiveOn)}` : 'Not open yet', tone: 'new' };
  }
  if (link.status === 'unpublished' && link.unpublishReason === 'job_on_hold') {
    return { phase: 'paused', label: 'Paused while the job is on hold', tone: 'hold' };
  }
  const isFull = link.maxApplications !== null && link.applicants >= link.maxApplications;
  if (link.status !== 'published' || isFull) return closed(link, isFull);
  if (!link.expiresOn) return { phase: 'open', label: 'Open · no end date', tone: 'live' };

  const daysLeft = daysBetween(today, link.expiresOn);
  if (daysLeft < 0) return closed({ ...link, closedOn: link.expiresOn }, false);
  if (daysLeft === 0) return { phase: 'closing', label: 'Closes today', tone: 'attention' };
  if (daysLeft === 1) return { phase: 'closing', label: 'Closes tomorrow', tone: 'attention' };
  return { phase: 'open', label: `Open · ${daysLeft} days left`, tone: 'live' };
}

function closed(link: Pick<PostingView, 'closedOn'>, isFull: boolean): FormLinkStatus {
  if (isFull) return { phase: 'closed', label: 'Closed · limit reached', tone: 'ended' };
  return { phase: 'closed', label: link.closedOn ? `Closed ${formatIsoDate(link.closedOn)}` : 'Closed', tone: 'ended' };
}

/** Whole days from one ISO date to another. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / MS_PER_DAY);
}

/** An ISO date plus some days. */
export function addDays(isoDate: string, days: number): string {
  return new Date(Date.parse(`${isoDate}T00:00:00Z`) + days * MS_PER_DAY).toISOString().slice(0, 10);
}

/** Today's date in Bangladesh: link dates are BD calendar days (D14). */
export function bdToday(now: Date = new Date()): string {
  return isoToDhaka(now.toISOString()).date;
}

/** The instant a link closes, for showing in the viewer's own zone with its abbreviation (#8). */
export function closingInstant(expiresOn: string): Date {
  return new Date(`${expiresOn}T${LINK_CLOSING_CLOCK}:00${BD_OFFSET}`);
}
